import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const AGENTS = [
  {
    id: "bizcard",
    category: "Contact Intelligence",
    name: "Business Card Agent",
    description: "Scan business cards with AI, auto-research companies, and manage your contact network with semantic search.",
    path: "/bizcard",
    accent: "#2563eb",
    icon: "📇",
    status: "active",
  },
  {
    id: "compliance",
    category: "Procurement",
    name: "Compliance Bidding Agent",
    description: "Upload RFQ documents, supplier quotes, and your quotation — get a full 11-point compliance report before submission.",
    path: "/compliance",
    accent: "#7c3aed",
    icon: "📋",
    status: "active",
  },
  {
    id: "bni",
    category: "Networking",
    name: "BNI Chapter Advisor",
    description: "Find the BNI chapters worth joining or visiting in any city — matched to your business and referral partners, with an Excel member list.",
    path: "/bni",
    accent: "#0f766e",
    icon: "🤝",
    status: "active",
  },
  {
    id: "expense",
    category: "Finance",
    name: "Personal Expense Manager",
    description: "Photograph receipts, auto-categorise items with AI, track budgets, and surface spending insights.",
    path: "/expense",
    accent: "#059669",
    icon: "💰",
    status: "active",
  },
  {
    id: "hrms",
    category: "HR & Payroll",
    name: "HRMS & Payroll",
    description: "Maintain a centralised employee record for every hire, with AI-assisted onboarding and Oman WPS-ready payroll on the way.",
    path: "/hrms/dashboard",
    accent: "#0284c7",
    icon: "🧑‍💼",
    status: "active",
  },
   {
    id: "admin",
    category: "Administration",
    name: "Admin Panel",
    description: "Manage users, roles, and system settings. Monitor platform activity and configure organisation-wide preferences.",
    path: "/admin",
    accent: "#b45309",
    icon: "⚙️",
    status: "active",
  },
];

const STATUS = {
  active: { label: "Active", color: "#059669", bg: "rgba(16,185,129,.1)", border: "rgba(16,185,129,.25)" },
  soon:   { label: "Coming Soon", color: "#b45309", bg: "rgba(245,158,11,.08)", border: "rgba(245,158,11,.2)" },
};

export default function Home() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg:        #f0f4fc;
          --surface:   #ffffff;
          --surface-2: #f4f8ff;
          --border:    #c0d0eb;
          --border-2:  #d5e3f5;
          --accent:    #2563eb;
          --text:      #0d1a36;
          --text-2:    #2a4a78;
          --muted:     #6080aa;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
          color-scheme: light;
        }

        body { background: var(--bg); color: var(--text); }

        /* ── Top bar ── */
        .topbar {
          position: sticky;
          top: 0;
          z-index: 100;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 32px;
          background: rgba(240,244,252,.92);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border-2);
        }

        .brand {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }
        .brand-mark {
          width: 30px;
          height: 30px;
          border-radius: 7px;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
        }
        .brand-label {
          font-size: 14px;
          font-weight: 600;
          letter-spacing: -0.01em;
          color: var(--text);
        }

        .topbar-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .org-chip {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 20px;
          font-size: 12px;
          color: var(--text-2);
        }
        .org-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #059669;
          flex-shrink: 0;
        }
        .avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          color: #fff;
          flex-shrink: 0;
          cursor: pointer;
        }
        .logout-btn {
          background: none;
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 5px 12px;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-2);
          cursor: pointer;
          letter-spacing: 0.01em;
          transition: border-color .15s, color .15s;
        }
        .logout-btn:hover { border-color: #dc2626; color: #dc2626; }

        /* ── Main ── */
        .main {
          max-width: 1100px;
          margin: 0 auto;
          padding: 52px 32px 80px;
        }

        .welcome-row {
          margin-bottom: 52px;
        }
        .welcome-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 10px;
        }
        .welcome-name {
          font-size: 28px;
          font-weight: 700;
          letter-spacing: -0.025em;
          color: var(--text);
          margin-bottom: 6px;
        }
        .welcome-sub {
          font-size: 14px;
          color: var(--text-2);
          line-height: 1.6;
        }

        /* ── Section header ── */
        .section-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin-bottom: 24px;
        }
        .section-title {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.07em;
          text-transform: uppercase;
          color: var(--muted);
        }
        .section-count {
          font-size: 12px;
          color: var(--muted);
          font-variant-numeric: tabular-nums;
        }

        /* ── Agent grid ── */
        .agent-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        /* ── Agent card ── */
        .agent-card {
          position: relative;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transition: border-color .2s, transform .2s, box-shadow .2s;
          cursor: default;
        }
        .agent-card:not(.disabled):hover {
          border-color: var(--card-accent);
          transform: translateY(-2px);
          box-shadow: 0 8px 32px rgba(0,0,0,.3), 0 0 0 1px var(--card-accent);
        }
        .agent-card.disabled { opacity: 0.5; }

        .card-stripe {
          height: 3px;
          background: var(--card-accent);
          flex-shrink: 0;
        }
        .card-glow {
          position: absolute;
          top: 3px;
          left: 0; right: 0;
          height: 120px;
          background: radial-gradient(ellipse 80% 100% at 50% 0%, var(--card-glow) 0%, transparent 80%);
          pointer-events: none;
        }

        .card-body {
          padding: 24px 24px 20px;
          flex: 1;
          position: relative;
        }

        .card-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 18px;
        }
        .card-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: var(--surface-2, #f4f8ff);
          border: 1px solid var(--border-2);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          flex-shrink: 0;
        }
        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 8px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          border: 1px solid;
          white-space: nowrap;
        }
        .status-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: currentColor;
          flex-shrink: 0;
        }

        .card-category {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--card-accent);
          margin-bottom: 6px;
        }
        .card-name {
          font-size: 16px;
          font-weight: 700;
          letter-spacing: -0.015em;
          color: var(--text);
          margin-bottom: 10px;
          text-wrap: balance;
        }
        .card-desc {
          font-size: 13px;
          line-height: 1.65;
          color: var(--text-2);
        }

        .card-footer {
          padding: 16px 24px;
          border-top: 1px solid var(--border-2);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .launch-btn {
          background: none;
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 7px 16px;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-2);
          cursor: pointer;
          letter-spacing: 0.01em;
          transition: background .15s, border-color .15s, color .15s;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .agent-card:not(.disabled):hover .launch-btn {
          background: var(--card-accent);
          border-color: var(--card-accent);
          color: #fff;
        }
        .launch-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .launch-arrow {
          font-size: 11px;
          transition: transform .15s;
        }
        .agent-card:not(.disabled):hover .launch-arrow {
          transform: translateX(3px);
        }

        /* ── Footer ── */
        .page-footer {
          max-width: 1100px;
          margin: 60px auto 0;
          padding: 24px 32px;
          border-top: 1px solid var(--border-2);
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .footer-brand {
          font-size: 12px;
          color: var(--muted);
          font-weight: 500;
        }
        .footer-note {
          font-size: 11px;
          color: var(--muted);
          opacity: 0.6;
        }

        @media (max-width: 640px) {
          .topbar { padding: 0 16px; }
          .main { padding: 32px 16px 60px; }
          .org-chip { display: none; }
          .welcome-name { font-size: 22px; }
        }
      `}</style>

      {/* Top bar */}
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">🤖</div>
          <span className="brand-label">AI Agent Portal</span>
        </div>
        <div className="topbar-right">
          {user?.org_name && (
            <div className="org-chip">
              <span className="org-dot" />
              {user.org_name}
            </div>
          )}
          <div className="avatar" title={user?.name}>{initials}</div>
          <button className="logout-btn" onClick={logout}>Sign out</button>
        </div>
      </header>

      {/* Main content */}
      <main className="main">
        <div className="welcome-row">
          <div className="welcome-eyebrow">AI Agent Portal</div>
          <h1 className="welcome-name">Welcome back, {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="welcome-sub">Select an agent below to get started. Each agent is purpose-built for a specific business workflow.</p>
        </div>

        <div className="section-header">
          <span className="section-title">Available Agents</span>
          <span className="section-count">
            {AGENTS.filter(a => a.status === "active").length} active
            {AGENTS.some(a => a.status === "soon") && ` · ${AGENTS.filter(a => a.status === "soon").length} coming soon`}
          </span>
        </div>

        <div className="agent-grid">
          {AGENTS.map(agent => {
            const s = STATUS[agent.status];
            const glow = agent.accent + "18";
            return (
              <div
                key={agent.id}
                className={`agent-card${agent.status === "soon" ? " disabled" : ""}`}
                style={{ "--card-accent": agent.accent, "--card-glow": glow }}
                onClick={() => agent.status === "active" && navigate(agent.path)}>
                <div className="card-stripe" />
                <div className="card-glow" />
                <div className="card-body">
                  <div className="card-top">
                    <div className="card-icon">{agent.icon}</div>
                    <div className="status-pill"
                         style={{ color: s.color, background: s.bg, borderColor: s.border }}>
                      <span className="status-dot" />
                      {s.label}
                    </div>
                  </div>
                  <div className="card-category">{agent.category}</div>
                  <div className="card-name">{agent.name}</div>
                  <p className="card-desc">{agent.description}</p>
                </div>
                <div className="card-footer">
                  <button className="launch-btn" disabled={agent.status === "soon"}>
                    {agent.status === "soon" ? "Coming soon" : "Launch agent"}
                    {agent.status === "active" && <span className="launch-arrow">→</span>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <footer className="page-footer">
        <span className="footer-brand">🤖 Falcon Pro · AI Agent Portal</span>
        <span className="footer-note">Powered by GPT-4o · Data stays within your organisation</span>
      </footer>
    </>
  );
}

